import { Box, Heading, Icons, Text, ThemeContext } from '@vouched.id/vault';
import React, { useContext, useEffect, useState } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { AMLCaption } from 'components/account/jobs-v2/dropdowns/AML/AMLCaption';
import { AmlLinkIcon, AmlLinkParentStyles, AmlLinkStyles } from 'components/account/jobs-v2/dropdowns/AML/AmlLinkIcon';
import { AmlMonitoringStatusBar } from 'components/account/jobs-v2/dropdowns/AML/AmlMonitoringStatusBar';
import { getRequestItemEntries, getRiskTableEntries } from 'components/account/jobs-v2/dropdowns/AML/amlV2Utils';
import { MultiSpanTable } from 'components/account/jobs-v2/dropdowns/MultiSpanTable';
import { SingleSpanTable } from 'components/account/jobs-v2/dropdowns/SingleSpanTable';
import { useGlobalState } from 'store/reducers/reducer';
import { getAmlV2Job } from 'store/sagas/sagas';
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const calculateMonitoringStopDate = (startDate, durationDays) => {
    return new Date(startDate.getTime() + durationDays * MILLISECONDS_PER_DAY);
};
const MonitoringCard = ({ title, text }) => (React.createElement(Box, { flex: ['1'], paddingX: "spacing.default", paddingY: "spacing.default", style: {
        borderRadius: '6px',
        border: '1px solid #C6C6CA',
        boxSizing: 'border-box',
        backgroundColor: '#FFFFFF'
    } },
    React.createElement(Text, null, title.toUpperCase()),
    React.createElement(Text, null, text)));
const BackToAmlJobLink = ({ jobSid, submittedAt, isAdminRoute }) => (React.createElement(Box, { display: "flex", justifyContent: "space-between", marginBottom: "spacing.large", paddingX: "spacing.large", paddingY: "spacing.default", style: AmlLinkParentStyles },
    React.createElement(NavLink, { style: AmlLinkStyles, to: `${isAdminRoute ? '/admin' : '/account'}/jobs/${jobSid}` },
        React.createElement(Text, null, "View initial AML results "),
        React.createElement(AmlLinkIcon, null)),
    submittedAt && React.createElement(Text, null, submittedAt.toLocaleString())));
export const AmlMonitoringPage = () => {
    const { asyncDispatch } = useGlobalState();
    const { id: jobSid } = useParams();
    const location = useLocation();
    const [amlV2Job, setAmlV2Job] = useState(location.state?.job);
    const { tokens } = useContext(ThemeContext).theme;
    const isAdminRoute = location.pathname.includes('/admin');
    useEffect(() => {
        if (!amlV2Job) {
            asyncDispatch(getAmlV2Job(jobSid)).then(setAmlV2Job, console.error);
        }
    }, [jobSid]);
    if (!amlV2Job) {
        return React.createElement(Icons.Loading, null);
    }
    if (amlV2Job.monitoring === null) {
        return (React.createElement(Box, { display: ['block'], paddingX: "spacing.xlarge", paddingY: "spacing.large", style: { boxSizing: 'border-box' }, tag: "main", width: "100%" },
            React.createElement(Box, { marginBottom: "spacing.large" },
                React.createElement(Heading, { style: {
                        fontWeight: tokens.text.weight.bold
                    }, tag: "h1", variation: "h3" }, "Jobs")),
            React.createElement(Box, { width: "70%" },
                React.createElement(Box, { marginBottom: "spacing.large" },
                    React.createElement(Heading, { style: {
                            fontWeight: tokens.text.weight.bold
                        }, tag: "h2", variation: "h4" }, "AML Monitoring")),
                React.createElement(Text, { style: { marginBottom: 'spacing.large' } },
                    "The requested AML job ",
                    jobSid,
                    " has either never been monitored or experienced an error during initial screening. Please contact support if needed."),
                React.createElement(BackToAmlJobLink, { isAdminRoute: isAdminRoute, jobSid: jobSid }))));
    }
    const { startedAt, endedAt, durationDays, risks } = amlV2Job.monitoring;
    const monitoringStartedAtDate = new Date(startedAt);
    const monitoringEndedAtDate = endedAt ? new Date(endedAt) : null;
    const status = monitoringEndedAtDate ? 'completed' : 'active';
    return (React.createElement(Box, { "data-test": "job-v2-view", display: ['block'], paddingX: "spacing.xlarge", paddingY: "spacing.large", style: { boxSizing: 'border-box' }, tag: "main", width: "100%" },
        React.createElement(Box, { marginBottom: "spacing.large" },
            React.createElement(Heading, { style: {
                    fontWeight: tokens.text.weight.bold
                }, tag: "h1", variation: "h3" }, "Jobs")),
        React.createElement(Box, { display: "flex", flexWrap: "wrap", marginBottom: "spacing.large", style: { gap: '15px' } },
            React.createElement(Box, { backgroundColor: "colors.white", flex: ['1 0 70%', '0 0 70%'], paddingX: "spacing.small", paddingY: "spacing.large", style: {
                    borderRadius: '10px',
                    boxShadow: tokens.shadows.hover,
                    boxSizing: 'border-box'
                } },
                React.createElement(AmlMonitoringStatusBar, { status: status, submittedAt: monitoringStartedAtDate, updatedAt: monitoringEndedAtDate ??
                        (risks[0]
                            ? new Date(risks[0].receivedAt)
                            : monitoringStartedAtDate) }))),
        React.createElement(Box, { width: "70%" },
            React.createElement(Box, { marginBottom: "spacing.large" },
                React.createElement(Heading, { style: {
                        fontWeight: tokens.text.weight.bold
                    }, tag: "h2", variation: "h4" }, "AML Monitoring")),
            React.createElement(AMLCaption, null),
            React.createElement(BackToAmlJobLink, { isAdminRoute: isAdminRoute, jobSid: jobSid, submittedAt: new Date(amlV2Job.initialScreening.submittedAt) }),
            React.createElement(Box, { display: "flex", style: { gap: '10px' } },
                React.createElement(MonitoringCard, { text: durationDays === null ? 'Indefinite' : `${durationDays} Days`, title: "DURATION" }),
                React.createElement(MonitoringCard, { text: monitoringStartedAtDate.toLocaleString(), title: "START" }),
                React.createElement(MonitoringCard, { text: monitoringEndedAtDate?.toLocaleString() ??
                        (durationDays === null
                            ? 'N/A'
                            : calculateMonitoringStopDate(monitoringStartedAtDate, durationDays).toLocaleString()), title: "STOP" })),
            React.createElement(SingleSpanTable, { header: "Submitted", items: getRequestItemEntries(amlV2Job.request), numColumns: 2 }),
            React.createElement(MultiSpanTable, { entries: [
                    ...(monitoringEndedAtDate
                        ? [
                            {
                                title: monitoringEndedAtDate.toLocaleString(),
                                items: [{ label: 'Monitoring', value: 'Ended' }]
                            }
                        ]
                        : []),
                    ...getRiskTableEntries(risks),
                    {
                        title: monitoringStartedAtDate.toLocaleString(),
                        items: [{ label: 'Monitoring', value: 'Started' }]
                    }
                ], header: "Activity Log" }))));
};
